import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import { TabsUI } from '../Interface';
import {
  getInventoryConversionBinLabel,
  getInventoryConversionIssues,
  getInventoryConversionItemLabel,
  getInventoryConversionLines,
  getInventoryConversionReceipts,
  getInventoryConversionUnitCode,
  getInventoryConversionWarehouseLabel,
} from './inventoryConversionHelper';

const EmptyMessage = ({ text }) => (
  <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed', bgcolor: 'grey.50' }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Paper>
);

const InventoryLineCard = ({ line }) => {
  const itemLabel = getInventoryConversionItemLabel(line);
  const warehouseLabel = getInventoryConversionWarehouseLabel(line);
  const binLabel = getInventoryConversionBinLabel(line);
  const qty = line?.qty ?? '';
  const unitCode = getInventoryConversionUnitCode(line);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {itemLabel || 'Ismeretlen tétel'}
      </Typography>

      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Mennyiség: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{[qty, unitCode].filter(Boolean).join(' ')}</Box>
        </Typography>
        {warehouseLabel ? (
          <Typography variant="body2" color="text.secondary">
            Raktár: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{warehouseLabel}</Box>
          </Typography>
        ) : null}
        {binLabel ? (
          <Typography variant="body2" color="text.secondary">
            Tárhely: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{binLabel}</Box>
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
};

const MovementSection = ({ title, color, rows, emptyText }) => {
  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color }}>
        {title}
      </Typography>

      {rows.length === 0 ? (
        <EmptyMessage text={emptyText} />
      ) : (
        rows.map((row, index) => {
          const itemLabel = getInventoryConversionItemLabel(row);
          const warehouseLabel = getInventoryConversionWarehouseLabel(row);
          const binLabel = getInventoryConversionBinLabel(row);
          const qty = row?.batchQty ?? row?.qty ?? '';
          const unitCode = row?.quantityUnitCode ?? row?.item?.quantityUnit?.code ?? '';
          const docNumber = row?.doc?.docNum ?? row?.docNumber ?? row?.docNum ?? '';

          return (
            <Paper key={`${title}-${row?.docId ?? row?.id ?? row?.lineNum ?? index}`} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {itemLabel || 'Ismeretlen tétel'}
              </Typography>

              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {docNumber ? (
                  <Typography variant="body2" color="text.secondary">
                    Bizonylat: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{docNumber}</Box>
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  Mennyiség: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{[qty, unitCode].filter(Boolean).join(' ')}</Box>
                </Typography>
                {warehouseLabel ? (
                  <Typography variant="body2" color="text.secondary">
                    Raktár: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{warehouseLabel}</Box>
                  </Typography>
                ) : null}
                {binLabel ? (
                  <Typography variant="body2" color="text.secondary">
                    Tárhely: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{binLabel}</Box>
                  </Typography>
                ) : null}
                {row?.batchSn ? (
                  <Typography variant="body2" color="text.secondary">
                    Sarzs: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{row.batchSn}</Box>
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          );
        })
      )}
    </Stack>
  );
};

const InventoryConversionTabs = ({ selectedValues }) => {
  const [activeTab, setActiveTab] = useState(0);

  const lines = useMemo(() => getInventoryConversionLines(selectedValues), [selectedValues]);
  const issues = useMemo(() => getInventoryConversionIssues(selectedValues), [selectedValues]);
  const receipts = useMemo(() => getInventoryConversionReceipts(selectedValues), [selectedValues]);

  useEffect(() => {
    setActiveTab(0);
  }, [selectedValues?.id]);

  return (
    <Box sx={{ mt: 2 }}>
      <TabsUI key={selectedValues?.id ?? 'empty'} activeTab={activeTab} handleTabClick={setActiveTab} alignToStart>
        <div name="Tételek">
          <Stack spacing={1.25}>
            {lines.length === 0 ? (
              <EmptyMessage text="Ehhez a művelethez nem tartozik megjeleníthető tétel." />
            ) : (
              lines.map((line, index) => <InventoryLineCard key={line?.id ?? `${line?.itemCode ?? 'line'}-${index}`} line={line} />)
            )}
          </Stack>
        </div>

        <div name="Kiadás / Bevétel">
          <Stack spacing={2}>
            <MovementSection title="Kiadás" color="#c62828" rows={issues} emptyText="Ehhez a művelethez nincs kiadás rögzítve." />
            <Divider />
            <MovementSection title="Bevétel" color="#2e7d32" rows={receipts} emptyText="Ehhez a művelethez nincs bevétel rögzítve." />
          </Stack>
        </div>
      </TabsUI>
    </Box>
  );
};

export default InventoryConversionTabs;
